import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Lock } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import GoldRule from '../components/GoldRule';

export default function AdminLogin() {
  const { login } = useAdminAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center bg-mb"
      style={{ background: 'var(--mb-bg)' }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md px-8 py-12"
      >
        {/* Header */}
        <div className="text-center mb-10">
          <div className="eyebrow text-mb-muted mb-3 tracking-[0.25em]">MR. BOSS &amp; CO.</div>
          <h1 className="font-display text-4xl text-mb leading-[0.95] mb-2">
            The <span className="italic">Atelier</span>
          </h1>
          <div className="mt-6"><GoldRule size="sm" /></div>
          <p className="mt-6 font-ui text-[13px] text-mb-muted">
            Private access only. Enter your credentials to continue.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="eyebrow text-mb-muted mb-2 block" htmlFor="login-email">
              Email
            </label>
            <input
              id="login-email"
              data-testid="login-email-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="
                w-full bg-mb-surface hairline border-subtle
                font-ui text-[14px] text-mb placeholder-mb-muted
                px-4 py-3 focus:outline-none focus:border-gold
                transition-colors duration-200
              "
              placeholder="admin@mrbossco.com"
            />
          </div>

          <div>
            <label className="eyebrow text-mb-muted mb-2 block" htmlFor="login-password">
              Password
            </label>
            <input
              id="login-password"
              data-testid="login-password-input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="
                w-full bg-mb-surface hairline border-subtle
                font-ui text-[14px] text-mb placeholder-mb-muted
                px-4 py-3 focus:outline-none focus:border-gold
                transition-colors duration-200
              "
              placeholder="••••••••••••"
            />
          </div>

          {error && (
            <motion.div
              data-testid="login-error"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="hairline border-gold/50 bg-mb-surface px-4 py-3 flex items-center gap-2"
            >
              <Lock size={13} className="text-gold shrink-0" />
              <span className="font-ui text-[12px] text-mb-muted">{error}</span>
            </motion.div>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="
              w-full mt-2 py-3.5 px-6 bg-gold text-mb-bg
              font-ui text-[12px] tracking-[0.2em] uppercase
              flex items-center justify-center gap-2
              disabled:opacity-60 hover:opacity-90
              transition-opacity duration-200
            "
          >
            {loading ? (
              <><Loader2 size={14} className="animate-spin" /> Authenticating</>
            ) : (
              'Enter the Atelier'
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
